import React, { FC } from 'react';

import { useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { Hits } from 'react-instantsearch';
import { Link } from 'react-router-dom';

import ChevronTopIcon from '~/assets/icons/ChevronTopIcon';
import { FILTERS } from '~/const';
import useConfigureRelatedItems from '~/hooks/useConfigureRelatedItems';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import type { AggregatorHit } from '~/pages/DiscoverPage/const';
import { transformGlossaryTerm } from '~/pages/DiscoverPage/pages/TermPage/helpers';
import { HITS_TYPES } from '~/pages/const';
import { buildGlossaryRoute } from '~/routes';
import { LARGE_MARGIN_PX, SMALL_MARGIN_PX } from '~/theme';
import { GlossaryTerm } from '~/types';
import getAttributeByLocale from '~/utils/getAttributeByLocale';

const MATCHING_PATTERNS = {
  [FILTERS.Subcategory]: { score: 2 },
  [FILTERS.Category]: { score: 1 },
  [FILTERS.SearchType]: { score: 3 },
};

interface RelatedTermsProps {
  term?: GlossaryTerm | null;
  tenant?: string;
}

const RelatedTerms: FC<RelatedTermsProps> = ({ term, tenant }) => {
  const currentHit = transformGlossaryTerm(term, tenant);
  const theme = useTheme();
  const { i18n } = useTenantTranslation();

  useConfigureRelatedItems({
    hit: currentHit,
    matchingPatterns: MATCHING_PATTERNS,
  });

  return (
    <Wrapper>
      <Hits<AggregatorHit>
        hitComponent={({ hit }) => {
          if (hit?.searchable_type?.toString() !== HITS_TYPES.GLOSSARY || hit?.slug === term?.slug)
            return null;
          return (
            <StyledLink data-testid="relatedTerm" to={buildGlossaryRoute(hit?.slug)}>
              <span>{getAttributeByLocale(hit?.title, i18n.language)}</span>
              <StyledChevronIcon color={theme.palette.common.blue} />
            </StyledLink>
          );
        }}
      />
    </Wrapper>
  );
};

export default RelatedTerms;

const Wrapper = styled('div')`
  & .ais-Hits-list {
    padding: 0;
    margin-bottom: ${LARGE_MARGIN_PX};
    list-style: none;

    & > li:not(:last-child) {
      margin-bottom: ${SMALL_MARGIN_PX};
    }
  }
`;
const StyledLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 16px;
  line-height: 1.2;
  color: ${({ theme }) => theme.palette.common.gray};
`;
const StyledChevronIcon = styled(ChevronTopIcon)`
  display: block;
  min-width: 13px;
  width: 13px;
  margin-top: 3px;
  margin-left: 10px;
  transform: rotate(90deg);
`;
